import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Lock, Unlock, CheckCircle2, BadgeIndianRupee } from "lucide-react";

const formatDT = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
const istNowLocal = () => new Date(Date.now() + 330 * 60000).toISOString().slice(0, 16);
const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

/** R-9 lock state + Lock / Unlock for one paid week in weekly_closings_ledger (Super Admin only). */
export function LockCell({ row, canEdit, onChanged }) {
  const [mode, setMode] = useState(null); // "lock" | "unlock"
  if (row.status !== "paid") {
    return (
      <div className="flex items-center gap-2 whitespace-nowrap">
        <span className="text-[10px] text-navy/70 italic" data-testid={`closing-lock-state-${row.period_label}`}>Locks after paid</span>
        {canEdit ? <MarkPaidButton row={row} onChanged={onChanged} /> : null}
      </div>
    );
  }
  const locked = row.locked;
  return (
    <div className="flex items-center gap-2 whitespace-nowrap">
      <span data-testid={`closing-lock-state-${row.period_label}`}
        className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm border ${locked ? "bg-navy/10 text-navy border-navy/30" : "bg-amber-50 text-amber-800 border-amber-300"}`}>
        {locked ? <Lock size={10} /> : <Unlock size={10} />} {locked ? "Locked" : `Unlocked${row.locked_rows ? ` ${row.locked_rows}/${row.paid_rows}` : ""}`}
      </span>
      {canEdit ? (
        <button type="button" onClick={() => setMode(locked ? "unlock" : "lock")} data-testid={`closing-${locked ? "unlock" : "lock"}-${row.period_label}`}
          className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 border rounded-sm transition-colors ${locked ? "border-red-300 text-red-700 hover:bg-red-50" : "border-royal/40 text-royal hover:bg-royal/5"}`}>
          {locked ? <><Unlock size={11} /> Unlock</> : <><Lock size={11} /> Lock</>}
        </button>
      ) : null}
      {mode ? (
        <ReasonModal
          title={mode === "lock" ? "Lock paid week" : "Unlock paid week"}
          note={mode === "lock" ? "Locked weeks cannot be changed. Recorded in the audit log." : "Write a signed operator statement (min 20 characters). Recorded in the audit log."}
          minLen={mode === "lock" ? 10 : 20}
          testid={`closing-${mode}-modal`}
          onCancel={() => setMode(null)}
          onSubmit={async (reason) => {
            await api.post(`/superadmin/${mode}-paid-period`, { period_label: row.period_label, reason });
            toast.success(mode === "lock" ? "Week locked (audit-logged)" : "Week unlocked (audit-logged)");
            setMode(null);
            onChanged();
          }}
        />
      ) : null}
    </div>
  );
}

/** Mark a finalized week paid after the bank transfers (Super Admin). Week is fixed; only the paid date/time is asked (IST). */
export function MarkPaidButton({ row, onChanged, big = false }) {
  const [open, setOpen] = useState(false);
  const [when, setWhen] = useState(istNowLocal);
  const [busy, setBusy] = useState(false);
  const id = row.period_label;
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/superadmin/mark-closing-paid", { period_label: id, paid_at: `${when}:00+05:30` });
      toast.success(`Week marked paid · ${data.modified_rows} members`);
      setOpen(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mark paid failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button type="button" onClick={() => { setWhen(istNowLocal()); setOpen(true); }} data-testid={big ? "closing-step-mark-paid" : `closing-mark-paid-${id}`}
        className={big ? STEP_BTN : "inline-flex items-center gap-1 text-[11px] px-2 py-1 border border-emerald-400 text-emerald-800 bg-emerald-50 hover:bg-emerald-100 rounded-sm transition-colors"}>
        <BadgeIndianRupee size={big ? 12 : 11} /> Mark paid
      </button>
      {open ? (
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-4 bg-navy/60 backdrop-blur-sm whitespace-normal" onClick={() => setOpen(false)} data-testid="closing-mark-paid-modal">
          <div className="w-full max-w-md bg-white rounded-sm border border-border shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-border">
              <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Audit-logged action</div>
              <h3 className="font-serif text-lg text-navy">Mark week paid</h3>
            </div>
            <div className="p-5 space-y-3 text-xs text-navy/80">
              <p data-testid="closing-mark-paid-summary"><span className="font-mono text-navy">{id}</span> · {row.members} members · net <strong className="text-navy">{inr(row.total_net_rs)}</strong></p>
              <p>Do this only after the bank transfers are sent. Then press Lock.</p>
              <label className="block">
                <span className="block text-[10px] uppercase tracking-widest font-bold text-navy/70 mb-1">Paid on (IST)</span>
                <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="closing-mark-paid-date"
                  className="w-full border border-border rounded-sm px-3 py-2 text-sm focus:border-royal outline-none" />
              </label>
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-xs text-navy/70 hover:text-navy" data-testid="closing-mark-paid-cancel">Cancel</button>
                <button type="button" onClick={submit} disabled={busy || !when} data-testid="closing-mark-paid-confirm"
                  className="px-4 py-2 text-xs bg-emerald-700 text-white hover:bg-emerald-800 rounded-sm disabled:opacity-40 transition-colors">Confirm paid</button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

const STEP_BTN = "inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold px-4 py-2 bg-emerald-700 text-white hover:bg-emerald-800 rounded-sm disabled:opacity-50 transition-colors";

/** Weekly Closing step button: Lock the paid week (Super Admin). */
export function LockStepButton({ row, onChanged }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="closing-step-lock" className={STEP_BTN.replace("bg-emerald-700", "bg-navy").replace("hover:bg-emerald-800", "hover:bg-navy/90")}>
        <Lock size={12} /> Lock
      </button>
      {open ? (
        <ReasonModal title="Lock paid week" note={`${row.period_label} · paid. Locked weeks cannot be changed. Recorded in the audit log.`} minLen={10}
          testid="closing-step-lock-modal" onCancel={() => setOpen(false)}
          onSubmit={async (reason) => {
            await api.post("/superadmin/lock-paid-period", { period_label: row.period_label, reason });
            toast.success("Week locked (audit-logged)");
            setOpen(false);
            onChanged();
          }} />
      ) : null}
    </>
  );
}

function ReasonModal({ title, note, minLen, testid, onCancel, onSubmit }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await onSubmit(reason.trim()); } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center p-4 bg-navy/60 backdrop-blur-sm whitespace-normal" onClick={onCancel} data-testid={testid}>
      <div className="w-full max-w-md bg-white rounded-sm border border-border shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-border">
          <div className="text-[10px] uppercase tracking-widest text-red-700 font-bold">Audit-logged action</div>
          <h3 className="font-serif text-lg text-navy">{title}</h3>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-navy/80">{note}</p>
          <textarea data-testid={`${testid}-reason`} value={reason} onChange={(e) => setReason(e.target.value)} rows={4}
            placeholder={`Reason (minimum ${minLen} characters)`} className="w-full border border-border rounded-sm px-3 py-2 text-sm focus:border-royal outline-none" />
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={onCancel} className="px-4 py-2 text-xs text-navy/70 hover:text-navy" data-testid={`${testid}-cancel`}>Cancel</button>
            <button type="button" onClick={submit} disabled={busy || reason.trim().length < minLen} data-testid={`${testid}-confirm`}
              className="px-4 py-2 text-xs bg-navy text-white hover:bg-navy/90 rounded-sm disabled:opacity-40 transition-colors">Confirm</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Old automatic-engine runs (db.closings) with their Lock / Unlock, restored as before. */
export function EngineRunLocks() {
  const [rows, setRows] = useState([]);
  const [busyRow, setBusyRow] = useState(null);
  const [unlockFor, setUnlockFor] = useState(null);
  const load = () => api.get("/admin/closings").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const doLock = async (id) => {
    if (!window.confirm(`Lock closing ${id}?\nOnce locked, its numbers cannot be edited without an audit-logged unlock action.`)) return;
    setBusyRow(id);
    try { await api.post(`/admin/closings/${id}/lock`); toast.success("Closing locked"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Lock failed"); }
    finally { setBusyRow(null); }
  };

  return (
    <div className="overflow-x-auto border-t border-border" data-testid="engine-runs">
      <div className="px-5 pt-3 pb-1 text-[10px] uppercase tracking-widest text-navy/70 font-bold">Automatic closing runs (old engine)</div>
      {rows.length === 0 ? (
        <div className="px-5 pb-4 text-xs text-navy/70 italic">No closing recorded</div>
      ) : (
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-navy/80">
            <tr>{["Run at", "Users", "Carry-fwd SP", "Pair ₹", "Bonus ₹", "Rank ↑", "Status", "Actions"].map((h) => (
              <th key={h} className="text-left px-4 py-2 font-semibold uppercase tracking-wider text-[10px]">{h}</th>))}</tr>
          </thead>
          <tbody className="text-navy">
            {rows.map((c) => (
              <tr key={c.id} data-testid={`closing-row-${c.id}`} className="border-t border-border/60 hover:bg-slate-50 transition-colors">
                <td className="px-4 py-2 whitespace-nowrap">{formatDT(c.ran_at)}</td>
                <td className="px-4 py-2">{c.users_processed}</td>
                <td className="px-4 py-2">{c.carry_forward_bv}</td>
                <td className="px-4 py-2">₹{(c.pair_income_credited || 0).toLocaleString("en-IN")}</td>
                <td className="px-4 py-2">₹{(c.monthly_bonus_credited || 0).toLocaleString("en-IN")}</td>
                <td className="px-4 py-2">{c.rank_upgrades}</td>
                <td className="px-4 py-2"><RunStatusPill status={c.status || "open"} /></td>
                <td className="px-4 py-2">
                  {c.status === "locked" ? (
                    <button type="button" onClick={() => setUnlockFor(c.id)} disabled={busyRow === c.id} data-testid={`closing-unlock-${c.id}`}
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-1 border border-red-300 text-red-700 hover:bg-red-50 rounded-sm disabled:opacity-50"><Unlock size={11} /> Unlock</button>
                  ) : (
                    <button type="button" onClick={() => doLock(c.id)} disabled={busyRow === c.id} data-testid={`closing-lock-${c.id}`}
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-1 border border-royal/40 text-royal hover:bg-royal/5 rounded-sm disabled:opacity-50"><Lock size={11} /> Lock</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {unlockFor ? (
        <ReasonModal title="Unlock closing" note="This is a permanent audit-log entry. Please state the reason for unlocking this closed period."
          minLen={10} testid="closing-unlock-modal" onCancel={() => setUnlockFor(null)}
          onSubmit={async (reason) => {
            await api.post(`/admin/closings/${unlockFor}/unlock`, { reason });
            toast.success("Closing unlocked (audit-logged)");
            setUnlockFor(null);
            load();
          }} />
      ) : null}
    </div>
  );
}

function RunStatusPill({ status }) {
  const locked = status === "locked";
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border ${locked ? "bg-navy/10 text-navy border-navy/30" : "bg-emerald-50 text-emerald-800 border-emerald-200"}`}>
      {locked ? <Lock size={10} /> : <CheckCircle2 size={10} />} {locked ? "Locked" : "Open"}
    </span>
  );
}
